import { Button } from '@trukt/ui/Button';
import { TextField } from '@trukt/ui/TextField';
import { CloudOff, FileOutput, LoaderCircle, Save, Trash2 } from '@trukt/ui/icons';
import { useState } from 'react';
import {
  ReportSaveFormProps,
  ReportFormValues,
  ReportSaveFormTriggeredActions,
} from './ReportSaveForm.types';

import { RenderWhen } from '@trukt/ui/RenderWhen';

const REPORT_FORM_INITIAL_VALUES: ReportFormValues = {
  isPrivate: true,
  reportName: '',
};

export const ReportSaveForm = (props: ReportSaveFormProps) => {
  const initialValues = props.initialValues ?? REPORT_FORM_INITIAL_VALUES;

  return (
    <ReportSaveFormFields
      {...props}
      key={`${Number(initialValues.isPrivate)}:${initialValues.reportName}`}
      initialValues={initialValues}
    />
  );
};

function ReportSaveFormFields({
  variant,
  initialValues,
  onSubmit,
  loadingStates,
  renderCancelExportButton,
}: ReportSaveFormProps & { initialValues: ReportFormValues }) {
  const [state, setState] = useState<ReportFormValues>({ ...initialValues });

  const isValidName = Boolean(state.reportName) && state.reportName.trim()?.length > 0;

  const resetForm = () => {
    setState({ ...REPORT_FORM_INITIAL_VALUES });
  };

  const handleSubmit = (triggeredAction: ReportSaveFormTriggeredActions) => {
    onSubmit({ triggeredAction, values: state, resetForm });
  };

  return (
    <section className="flex items-center gap-4">
      {variant === 'create' && (
        <form
          id="create-report"
          onSubmit={(e) => {
            e.preventDefault();
            if (!state.reportName.trim()) return false;

            handleSubmit('save');
            return true;
          }}
        >
          <TextField
            value={state.reportName}
            onChange={({ target }) => setState({ ...state, reportName: target.value })}
            onBlur={({ target }) => setState({ ...state, reportName: target.value })}
            placeholder="Report Name"
            iconPosition="right"
          />
        </form>
      )}

      {variant === 'create' && (
        <Button
          data-testid="save-button"
          isLoading={loadingStates?.isCreating}
          size="md"
          color="secondary"
          variant="transparent"
          name="Save Report"
          aria-label="Save Report"
          type="submit"
          form="create-report"
          className="flex items-center justify-center"
          disabled={variant === 'create' && !isValidName}
        >
          <RenderWhen
            condition={Boolean(loadingStates?.isCreating)}
            onFalse={
              <Save
                role="img"
                aria-label="Save report"
                size="1.2rem"
                className="pointer-events-none cursor-pointer stroke-primary-400"
              >
                <title>Save report</title>
              </Save>
            }
          >
            <LoaderCircle
              role="progressbar"
              aria-label="Button Loader"
              size="1.2rem"
              className="animate-spin cursor-pointer stroke-primary-400"
            >
              <title>Button Loader</title>
            </LoaderCircle>
          </RenderWhen>
        </Button>
      )}

      {variant === 'update' && (
        <>
          <Button
            data-testid="export-button"
            isLoading={loadingStates?.isStartingExport || loadingStates?.isExporting}
            size="md"
            variant="transparent"
            color="secondary"
            className="flex items-center justify-center"
            onClick={() => handleSubmit('export')}
            name="Export Report"
            aria-label="Export Report"
          >
            <RenderWhen
              condition={Boolean(loadingStates?.isStartingExport || loadingStates?.isExporting)}
              onFalse={
                <FileOutput
                  role="img"
                  aria-label="Export report"
                  size="1.2rem"
                  className="cursor-pointer stroke-primary-400"
                >
                  <title>Export report</title>
                </FileOutput>
              }
            >
              <LoaderCircle
                role="progressbar"
                aria-label="Button Loader"
                size="1.2rem"
                className="animate-spin cursor-pointer stroke-primary-400"
              >
                <title>Button Loader</title>
              </LoaderCircle>
            </RenderWhen>
          </Button>

          {renderCancelExportButton && (
            <Button
              data-testid="cancel-export-button"
              isLoading={loadingStates?.isCanceling}
              size="md"
              className="flex items-center justify-center"
              variant="transparent"
              color="error"
              onClick={() => handleSubmit('cancel-export')}
              name="Cancel Report"
              aria-label="Cancel Report"
            >
              <RenderWhen
                condition={Boolean(loadingStates?.isCanceling)}
                onFalse={
                  <CloudOff
                    role="img"
                    aria-label="Cancel report"
                    size="1.2rem"
                    className="cursor-pointer stroke-error-400"
                  >
                    <title>Cancel report</title>
                  </CloudOff>
                }
              >
                <LoaderCircle
                  role="progressbar"
                  aria-label="Button Loader"
                  size="1.2rem"
                  className="animate-spin cursor-pointer stroke-primary-400"
                >
                  <title>Button Loader</title>
                </LoaderCircle>
              </RenderWhen>
            </Button>
          )}
        </>
      )}

      {variant !== 'create' && (
        <Trash2
          role="img"
          aria-label="Delete report"
          size="1.2rem"
          className="cursor-pointer fill-secondary-700 stroke-primary-400"
          onClick={() => handleSubmit('delete')}
        >
          <title>Delete report</title>
        </Trash2>
      )}
    </section>
  );
}
